/**
 * Einzelbeiträge einer Kooperation (0068): Formate, Stände, Bezeichnung, Nutzungsrechte je Beitrag, „überfällig“, Filter der
 * Beitragsübersicht und Zusammenfassung als Text (Freitext der Kooperation, Vertragsplatzhalter {{deal.deliverables_list}}).
 * Gleich halten mit app.deliverable_formats, app.deliverable_format_label, app.deliverable_overdue_kind und der Spalte
 * usage_rights_until (0068): Oberfläche, Kalender und täglicher Lauf rechnen dieselben Fristen.
 */
import { addDaysIso, addMonthsIso } from "./contract-term.js";
import { formatContractDate } from "./contract-render.js";
import type { IsoDate, Language, MoneyLocale } from "./types.js";

export const DELIVERABLE_FORMATS = [
  "reel", "story", "story_set", "post", "carousel", "tiktok", "youtube_video", "short", "live", "ugc", "podcast", "blog", "newsletter", "other",
] as const;
export type DeliverableFormat = (typeof DELIVERABLE_FORMATS)[number];

export const DELIVERABLE_FORMAT_LABEL: Readonly<Record<DeliverableFormat, { de: string; en: string }>> = {
  reel: { de: "Reel", en: "Reel" },
  story: { de: "Story", en: "Story" },
  story_set: { de: "Story-Set", en: "Story set" },
  post: { de: "Post", en: "Post" },
  carousel: { de: "Karussell", en: "Carousel" },
  tiktok: { de: "TikTok-Video", en: "TikTok video" },
  youtube_video: { de: "YouTube-Video", en: "YouTube video" },
  short: { de: "Short", en: "Short" },
  live: { de: "Livestream", en: "Livestream" },
  ugc: { de: "UGC-Video", en: "UGC video" },
  podcast: { de: "Podcast-Folge", en: "Podcast episode" },
  blog: { de: "Blogartikel", en: "Blog post" },
  newsletter: { de: "Newsletter", en: "Newsletter" },
  other: { de: "Beitrag", en: "Content" },
};

/** Plattform, die zum Format passt (Vorschlag beim Anlegen); sonst die erste freigeschaltete der Agentur. */
export const DELIVERABLE_FORMAT_PLATFORM: Readonly<Partial<Record<DeliverableFormat, string>>> = {
  reel: "instagram", story: "instagram", story_set: "instagram", post: "instagram", carousel: "instagram",
  tiktok: "tiktok", youtube_video: "youtube", short: "youtube", podcast: "podcast", blog: "blog", newsletter: "newsletter", ugc: "ugc",
};

/** Anzeigename der Standard-Plattformen (Kalender im Portal und Kalender-Abo, KETTE-15); eigene Plattformen zeigen ihren Schlüssel. */
export const PLATFORM_NAMES: Readonly<Record<string, string>> = {
  instagram: "Instagram", tiktok: "TikTok", youtube: "YouTube", ugc: "UGC", podcast: "Podcast", twitch: "Twitch", linkedin: "LinkedIn", pinterest: "Pinterest",
  snapchat: "Snapchat", facebook: "Facebook", blog: "Blog", newsletter: "Newsletter",
};
export function platformName(key: string, lang: Language = "de"): string {
  return key === "other" ? (lang === "en" ? "Other" : "Weitere") : PLATFORM_NAMES[key] ?? key;
}

export const DELIVERABLE_STATUSES = ["planned", "draft_submitted", "changes_requested", "approved", "live", "cancelled"] as const;
export type DeliverableStatus = (typeof DELIVERABLE_STATUSES)[number];

export const DELIVERABLE_STATUS_LABEL: Readonly<Record<DeliverableStatus, { de: string; en: string }>> = {
  planned: { de: "Geplant", en: "Planned" },
  draft_submitted: { de: "Entwurf eingereicht", en: "Draft submitted" },
  changes_requested: { de: "Korrektur angefragt", en: "Changes requested" },
  approved: { de: "Freigegeben", en: "Approved" },
  live: { de: "Live", en: "Live" },
  cancelled: { de: "Entfallen", en: "Cancelled" },
};

export function isDeliverableFormat(v: unknown): v is DeliverableFormat {
  return typeof v === "string" && (DELIVERABLE_FORMATS as readonly string[]).includes(v);
}
export function isDeliverableStatus(v: unknown): v is DeliverableStatus {
  return typeof v === "string" && (DELIVERABLE_STATUSES as readonly string[]).includes(v);
}

/** Was eine Beitragszeile mindestens mitbringt (Tabelle deal_deliverables oder Sicht talent_deal_deliverables). */
export interface DeliverableLike {
  status: string;
  format: string;
  format_label?: string | null;
  platform: string;
  title?: string | null;
  draft_due?: string | null;
  publish_on?: string | null;
  published_on?: string | null;
  usage_rights_months?: number | null;
  /** 0199: Art der Nutzung, einfach/ausschließlich, Gebiet (freiwillig) */
  usage_kinds?: readonly string[] | null;
  usage_exclusive?: boolean | null;
  usage_territory?: string | null;
  label_checked?: boolean | null;
  /** 0073: Die eingereichte Fassung liegt per Freigabe-Link bei der Marke (offener Link) */
  at_brand?: boolean | null;
}

const clean = (v: string | null | undefined): string | null => (v && v.trim() ? v.trim() : null);

export function deliverableFormatLabel(format: string, custom: string | null | undefined, lang: Language = "de"): string {
  return clean(custom) ?? (isDeliverableFormat(format) ? DELIVERABLE_FORMAT_LABEL[format][lang] : format);
}

export function deliverableStatusLabel(status: string, lang: Language = "de"): string {
  return isDeliverableStatus(status) ? DELIVERABLE_STATUS_LABEL[status][lang] : status;
}

/**
 * „Reel · Instagram · Unboxing“. Die Plattform entfällt, wenn das Format sie schon nennt (TikTok-Video); ohne Bezeichnung aus
 * dem Katalog steht der Schlüssel. Wie app.deliverable_label.
 */
export function deliverableLabel(x: Pick<DeliverableLike, "format" | "format_label" | "platform" | "title">, platformLabel: (key: string) => string | null,
  lang: Language = "de", opts: { title?: boolean } = {}): string {
  const f = deliverableFormatLabel(x.format, x.format_label, lang);
  const p = platformLabel(x.platform) ?? x.platform;
  const parts = [f];
  if (p && !f.toLowerCase().includes(p.toLowerCase())) parts.push(p);
  if (opts.title !== false && clean(x.title)) parts.push(clean(x.title)!);
  return parts.join(" · ");
}

/** Letzter Tag der Nutzungsrechte: Veröffentlichung plus Monate minus ein Tag (wie die Spalte usage_rights_until). */
export function usageRightsUntil(publishedOn: string | null | undefined, months: number | null | undefined): IsoDate | null {
  if (!publishedOn || !months || months < 1) return null;
  // § 188 Abs. 3 BGB: fehlt der Tag mit derselben Zahl im Zielmonat (31.01. + 1 Monat), endet die Frist mit dessen letztem Tag
  const target = addMonthsIso(publishedOn, months);
  return Number(target.slice(8, 10)) < Number(publishedOn.slice(8, 10)) ? target : addDaysIso(target, -1);
}

export type OverdueKind = "draft" | "publish";

/** Überfällig: Veröffentlichung (Termin vorbei, nicht live) vor Entwurf (Frist vorbei, noch nichts eingereicht). */
export function overdueKind(x: Pick<DeliverableLike, "status" | "draft_due" | "publish_on">, today: string): OverdueKind | null {
  if (["planned", "draft_submitted", "changes_requested", "approved"].includes(x.status) && x.publish_on && x.publish_on < today) return "publish";
  if (x.status === "planned" && x.draft_due && x.draft_due < today) return "draft";
  return null;
}

/**
 * Nächste Frist, auf die es jetzt ankommt: Entwurf bis zur Einreichung, danach die Veröffentlichung; live und entfallen: keine.
 * `kind` sagt, was fällig ist, nicht welches Datum: Solange kein Entwurf eingereicht ist, ist der Entwurf fällig, auch wenn
 * nur ein Veröffentlichungstermin feststeht (dann spätestens bis dahin, `dateIs: "publish_on"`). Posten geht erst nach der
 * Freigabe (deliverable_report_live, CREATOR-R2-1).
 */
export function nextDue(x: Pick<DeliverableLike, "status" | "draft_due" | "publish_on">): { kind: OverdueKind; date: IsoDate; dateIs: "draft_due" | "publish_on" } | null {
  if (x.status === "planned" || x.status === "changes_requested") {
    if (x.draft_due) return { kind: "draft", date: x.draft_due, dateIs: "draft_due" };
    if (x.publish_on) return { kind: "draft", date: x.publish_on, dateIs: "publish_on" };
    return null;
  }
  if (x.status === "draft_submitted" || x.status === "approved") return x.publish_on ? { kind: "publish", date: x.publish_on, dateIs: "publish_on" } : null;
  return null;
}

/** Sonntag der Woche von `today` (Woche Montag bis Sonntag). */
export function weekEnd(today: string): IsoDate {
  const d = new Date(`${today.slice(0, 10)}T00:00:00Z`).getUTCDay();
  return addDaysIso(today, d === 0 ? 0 : 7 - d);
}

/** 0073: „zur Prüfung“ (eingereicht, beim Team) und „bei der Marke“ (offener Freigabe-Link) sind getrennt. */
export type DeliverableBucket = "overdue" | "due_week" | "in_review" | "with_brand" | "live_unchecked";
export const DELIVERABLE_BUCKETS: readonly DeliverableBucket[] = ["overdue", "due_week", "in_review", "with_brand", "live_unchecked"];

/** In welchen Filtern der Beitragsübersicht steht ein Beitrag? (mehrere möglich) */
export function deliverableBuckets(x: DeliverableLike, today: string): DeliverableBucket[] {
  const out: DeliverableBucket[] = [];
  if (x.status === "cancelled") return out;
  if (overdueKind(x, today)) out.push("overdue");
  else {
    const due = nextDue(x);
    if (due && due.date >= today && due.date <= weekEnd(today)) out.push("due_week");
  }
  if (x.status === "draft_submitted") out.push(x.at_brand ? "with_brand" : "in_review");
  if (x.status === "live" && !x.label_checked) out.push("live_unchecked");
  return out;
}

/** Termine für „mehrere gleiche anlegen“: ab den ersten Terminen jeweils `spacingDays` später (0 = alle am selben Tag). */
export function plannedSeries(count: number, first: { draft_due: string | null; publish_on: string | null }, spacingDays = 0): { draft_due: string | null; publish_on: string | null }[] {
  const n = Math.max(1, Math.min(50, Math.trunc(count) || 1));
  const step = Math.max(0, Math.trunc(spacingDays) || 0);
  return Array.from({ length: n }, (_, i) => ({
    draft_due: first.draft_due ? addDaysIso(first.draft_due, i * step) : null,
    publish_on: first.publish_on ? addDaysIso(first.publish_on, i * step) : null,
  }));
}

/** Gleiche Beiträge zusammenfassen: „2 × Reel · Instagram, 3 × Story · Instagram“ (ohne entfallene). Leere Liste: null. */
export function summarizeDeliverables(list: readonly DeliverableLike[], platformLabel: (key: string) => string | null, lang: Language = "de"): string | null {
  const groups = new Map<string, { label: string; n: number }>();
  for (const x of list) {
    if (x.status === "cancelled") continue;
    const label = deliverableLabel(x, platformLabel, lang);
    const g = groups.get(label);
    if (g) g.n += 1;
    else groups.set(label, { label, n: 1 });
  }
  if (!groups.size) return null;
  return [...groups.values()].map((g) => (g.n > 1 ? `${g.n} × ${g.label}` : g.label)).join(", ");
}

/**
 * Strukturierte Liste für Verträge ({{deal.deliverables_list}}), eine Zeile je Beitragsart mit Fristen:
 * „2 × Reel · Instagram: Entwurf bis 01.10.2026, Veröffentlichung am 15.10.2026, Nutzungsrechte 6 Monate ab Veröffentlichung“.
 * Gleiche Beiträge mit denselben Fristen stehen in einer Zeile. Ohne (nicht entfallene) Beiträge: null, dann gilt der Freitext.
 */
export function deliverablesListText(list: readonly DeliverableLike[], opts: { platformLabel: (key: string) => string | null; lang?: Language; locale: MoneyLocale }): string | null {
  const lang = opts.lang ?? "de";
  const en = lang === "en";
  const date = (d: string) => formatContractDate(d, opts.locale);
  const rows = new Map<string, { text: string; n: number }>();
  for (const x of list) {
    if (x.status === "cancelled") continue;
    const details: string[] = [];
    if (x.draft_due) details.push(en ? `draft by ${date(x.draft_due)}` : `Entwurf bis ${date(x.draft_due)}`);
    const pub = x.published_on ?? x.publish_on;
    if (pub) details.push(x.published_on ? (en ? `published on ${date(pub)}` : `veröffentlicht am ${date(pub)}`) : (en ? `publication on ${date(pub)}` : `Veröffentlichung am ${date(pub)}`));
    if (x.usage_rights_months) {
      const m = x.usage_rights_months;
      details.push(en ? `usage rights ${m} ${m === 1 ? "month" : "months"} from publication` : `Nutzungsrechte ${m} ${m === 1 ? "Monat" : "Monate"} ab Veröffentlichung`);
    }
    const label = deliverableLabel(x, opts.platformLabel, lang);
    const text = details.length ? `${label}: ${details.join(", ")}` : label;
    const r = rows.get(text);
    if (r) r.n += 1;
    else rows.set(text, { text, n: 1 });
  }
  if (!rows.size) return null;
  return [...rows.values()].map((r) => (r.n > 1 ? `${r.n} × ${r.text}` : r.text)).join("\n");
}

/** 0199: Arten der Nutzung je Beitrag */
export const USAGE_KINDS = ["organic", "paid", "whitelisting"] as const;
export type UsageKind = (typeof USAGE_KINDS)[number];
export const USAGE_TERRITORIES = ["de", "dach", "eu", "worldwide"] as const;
export type UsageTerritory = (typeof USAGE_TERRITORIES)[number];
const USAGE_KIND_TEXT: Record<UsageKind, Record<Language, string>> = {
  organic: { de: "organisch auf den Kanälen der Marke", en: "organic on the brand's channels" },
  paid: { de: "bezahlte Werbung (Paid Ads)", en: "paid advertising (paid ads)" },
  whitelisting: { de: "Whitelisting über den Account des Creators", en: "whitelisting via the creator's account" },
};
const USAGE_TERRITORY_TEXT: Record<UsageTerritory, Record<Language, string>> = {
  de: { de: "Deutschland", en: "Germany" },
  dach: { de: "Deutschland, Österreich und Schweiz", en: "Germany, Austria and Switzerland" },
  eu: { de: "Europäische Union", en: "European Union" },
  worldwide: { de: "weltweit", en: "worldwide" },
};
export const usageKindLabel = (k: string, lang: Language = "de"): string => USAGE_KIND_TEXT[k as UsageKind]?.[lang] ?? k;
export const usageTerritoryLabel = (k: string, lang: Language = "de"): string => USAGE_TERRITORY_TEXT[k as UsageTerritory]?.[lang] ?? k;
const joinAnd = (parts: string[], lang: Language): string =>
  parts.length <= 1 ? parts.join("") : `${parts.slice(0, -1).join(", ")} ${lang === "en" ? "and" : "und"} ${parts[parts.length - 1]}`;

/**
 * 0199: Zusatz zu den Nutzungsrechten eines Beitrags („bezahlte Werbung (Paid Ads), ausschließlich, Gebiet: Deutschland“).
 * Ohne Art und Gebiet und bei einfachen Rechten leer: der bisherige Vertragstext bleibt dann unverändert.
 */
export function usageRightsDetail(x: Pick<DeliverableLike, "usage_kinds" | "usage_exclusive" | "usage_territory">, lang: Language = "de"): string | null {
  const kinds = (x.usage_kinds ?? []).filter((k): k is UsageKind => (USAGE_KINDS as readonly string[]).includes(k));
  const territory = x.usage_territory && (USAGE_TERRITORIES as readonly string[]).includes(x.usage_territory) ? x.usage_territory : null;
  if (!kinds.length && !territory && !x.usage_exclusive) return null;
  const en = lang === "en";
  const parts: string[] = [];
  if (kinds.length) parts.push(joinAnd(USAGE_KINDS.filter((k) => kinds.includes(k)).map((k) => USAGE_KIND_TEXT[k][lang]), lang));
  parts.push(x.usage_exclusive ? (en ? "exclusive" : "ausschließlich") : (en ? "non-exclusive" : "einfach"));
  if (territory) parts.push(`${en ? "territory" : "Gebiet"}: ${USAGE_TERRITORY_TEXT[territory as UsageTerritory][lang]}`);
  return parts.join(", ");
}

/**
 * Nutzungsrechte aus den Beiträgen (VERTRAEGE-R3-07), damit § 2 (Beiträge) und § 7 (Nutzungsrechte) des Kooperationsvertrags
 * dieselbe Dauer nennen: je Beitragsart eine Zeile „2 × Reel · Instagram: 6 Monate ab Veröffentlichung“. `byPiece` nur, wenn
 * jeder nicht entfallene Beitrag eine Dauer hat; `months` ist die gemeinsame Dauer (sonst null). Ohne Beiträge mit Dauer gilt
 * der Freitext der Kooperation (`list` = Freitext).
 */
export function usageRightsFromPieces(list: readonly DeliverableLike[], freeText: string | null | undefined,
  opts: { platformLabel: (key: string) => string | null; lang?: Language }): { list: string | null; byPiece: boolean; months: number | null } {
  const lang = opts.lang ?? "de";
  const en = lang === "en";
  const active = list.filter((x) => x.status !== "cancelled");
  const byPiece = active.length > 0 && active.every((x) => !!x.usage_rights_months);
  const set = new Set(active.map((x) => x.usage_rights_months ?? null));
  const months = byPiece && set.size === 1 ? ([...set][0] ?? null) : null;
  if (!byPiece) return { list: clean(freeText), byPiece: false, months: null };
  const rows = new Map<string, number>();
  for (const x of active) {
    const m = x.usage_rights_months!;
    const detail = usageRightsDetail(x, lang);
    const text = `${deliverableLabel(x, opts.platformLabel, lang)}: ${en ? `${m} ${m === 1 ? "month" : "months"} from publication` : `${m} ${m === 1 ? "Monat" : "Monate"} ab Veröffentlichung`}${detail ? `, ${detail}` : ""}`;
    rows.set(text, (rows.get(text) ?? 0) + 1);
  }
  return { list: [...rows.entries()].map(([text, n]) => (n > 1 ? `${n} × ${text}` : text)).join("\n"), byPiece: true, months };
}

/**
 * Wert für {{deal.deliverables_list}}: die Liste der Beiträge und, wenn die Kooperation zusätzlich einen Freitext der Leistungen
 * hat (deals.deliverables), dieser als Zeile „Vereinbarter Umfang insgesamt“; ohne Beiträge nur der Freitext, ohne beides null
 * (die Prüfung vor dem Versand meldet dann die Lücke).
 */
export function deliverablesListOrText(list: readonly DeliverableLike[], freeText: string | null | undefined,
  opts: { platformLabel: (key: string) => string | null; lang?: Language; locale: MoneyLocale }): string | null {
  const liste = deliverablesListText(list, opts);
  const frei = clean(freeText);
  if (!liste) return frei;
  // Praxistest K-016 (13.10.2026): Stand im Freitext mehr als in den angelegten Beiträgen („1 Reel, 3 Stories“ bei nur einem
  // Beitrag „Reel“), fehlte der Rest im Vertrag. Der vereinbarte Umfang steht deshalb zusätzlich als eigene Zeile da.
  if (!frei) return liste;
  return `${liste}\n${opts.lang === "en" ? "Agreed scope in total" : "Vereinbarter Umfang insgesamt"}: ${frei.replace(/\s*\n+\s*/g, "; ")}`;
}
